import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-table WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, TableWebElement.ts.
 */
export class TableWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>layout</code> property.
   * The column sizing method used for the Table's columns.
   * @return The value of <code>layout</code> property.
   *
   */
  public getLayout(): Promise<string> {
    return this.getProperty<string>('layout');
  }

  /**
   * Gets the value of <code>columns</code> property.
   * The set of columns that can be displayed in the Table.
   * @return The value of <code>columns</code> property.
   *
   */
  public getColumns(): Promise<object> {
    return this.getProperty<object>('columns');
  }

  /**
   * Gets the value of <code>row</code> property.
   * A subset of attributes for controlling certain behaviors on a per row basis.
   * @return The value of <code>row</code> property.
   *
   */
  public getRow(): Promise<Row> {
    return this.getProperty<Row>('row');
  }

  /**
   * Gets the value of <code>horizontalGridVisible</code> property.
   * Controls the display of the Table's horizontal gridlines.
   * @return The value of <code>horizontalGridVisible</code> property.
   *
   */
  public getHorizontalGridVisible(): Promise<string> {
    return this.getProperty<string>('horizontalGridVisible');
  }

  /**
   * Gets the value of <code>verticalGridVisible</code> property.
   * Controls the display of the Table's vertical gridlines.
   * @return The value of <code>verticalGridVisible</code> property.
   *
   */
  public getVerticalGridVisible(): Promise<string> {
    return this.getProperty<string>('verticalGridVisible');
  }

  /**
   * Sets the value of <code>selected</code> property.
   * The selected rows and/or columns. See the Help documentation for more information.
   * @param selected The value to set for <code>selected</code>
   *
   */
  public changeSelected(selected: Selected): Promise<void> {
    return this.setProperty<Selected>('selected', selected);
  }

  /**
   * Gets the value of <code>selected</code> property.
   * The selected rows and/or columns. See the Help documentation for more information.
   * @return The value of <code>selected</code> property.
   *
   */
  public getSelected(): Promise<Selected> {
    return this.getProperty<Selected>('selected');
  }

  /**
   * Gets the value of <code>selectionMode</code> property.
   * Specifies whether row and/or column selection gestures are enabled on the Table, and the cardinality of each (single/multiple/multipleToggle/none).
   * @return The value of <code>selectionMode</code> property.
   *
   */
  public getSelectionMode(): Promise<SelectionMode> {
    return this.getProperty<SelectionMode>('selectionMode');
  }

  /**
   * Gets the value of <code>selectAllControl</code> property.
   * Controls the display of the Table's select all control when multiple or multipleToggle row selection is enabled.
   * @return The value of <code>selectAllControl</code> property.
   *
   */
  public getSelectAllControl(): Promise<string> {
    return this.getProperty<string>('selectAllControl');
  }

  /**
   * Sets the value of <code>columnOrder</code> property.
   * Display and order of columns. See the Help documentation for more information.
   * @param columnOrder The value to set for <code>columnOrder</code>
   *
   */
  public changeColumnOrder(columnOrder: Array<string>): Promise<void> {
    return this.setProperty<Array<string>>('columnOrder', columnOrder);
  }

  /**
   * Gets the value of <code>columnOrder</code> property.
   * Display and order of columns. See the Help documentation for more information.
   * @return The value of <code>columnOrder</code> property.
   *
   */
  public getColumnOrder(): Promise<Array<string>> {
    return this.getProperty<Array<string>>('columnOrder');
  }

  /**
   * Gets the value of <code>currentCellOverride</code> property.
   * The cell override to apply to the current cell of the Table. In order for this property to be honored, a new object instance must be set.
   * @return The value of <code>currentCellOverride</code> property.
   *
   */
  public getCurrentCellOverride(): Promise<object> {
    return this.getProperty<object>('currentCellOverride');
  }

  /**
   * Gets the value of <code>currentCell</code> property.
   * The cell currently being used as the target for keyboard gestures made on the Table.
   * @return The value of <code>currentCell</code> property.
   *
   */
  public getCurrentCell(): Promise<object> {
    return this.getProperty<object>('currentCell');
  }

  /**
   * Sets the value of <code>columnWidths</code> property.
   * The desired widths of table columns. A record mapping column keys to numbers representing pixel widths for each column.
   * @param columnWidths The value to set for <code>columnWidths</code>
   *
   */
  public changeColumnWidths(columnWidths: object): Promise<void> {
    return this.setProperty<object>('columnWidths', columnWidths);
  }

  /**
   * Gets the value of <code>columnWidths</code> property.
   * The desired widths of table columns. A record mapping column keys to numbers representing pixel widths for each column.
   * @return The value of <code>columnWidths</code> property.
   *
   */
  public getColumnWidths(): Promise<object> {
    return this.getProperty<object>('columnWidths');
  }

  /**
   * Gets the value of <code>scrollPolicyOptions</code> property.
   * Options related to the Table's fetching and scrolling behaviors.
   * @return The value of <code>scrollPolicyOptions</code> property.
   *
   */
  public getScrollPolicyOptions(): Promise<ScrollPolicyOptions> {
    return this.getProperty<ScrollPolicyOptions>('scrollPolicyOptions');
  }

  /**
   * Gets the value of <code>columnResizeBehavior</code> property.
   * The column resize behavior this Table will utilize when column resizing is enabled on a given column.
   * @return The value of <code>columnResizeBehavior</code> property.
   *
   */
  public getColumnResizeBehavior(): Promise<string> {
    return this.getProperty<string>('columnResizeBehavior');
  }

  /**
   * Gets the value of <code>contextMenuConfig</code> property.
   * Specifies a context menu configuration.
   * @return The value of <code>contextMenuConfig</code> property.
   *
   */
  public getContextMenuConfig(): Promise<ContextMenuConfig> {
    return this.getProperty<ContextMenuConfig>('contextMenuConfig');
  }
}
export interface Row {
  /**
   * The column key(s) to be used as the accessible row header(s) for assistive technologies. See the Help documentation for more information.
   */
  accessibleRowHeader: string | Array<string> | null;
}
export interface Selected {
  /**
   * The selected columns. See the Help documentation for more information.
   */
  column: object;
  /**
   * The selected rows. See the Help documentation for more information.
   */
  row: object;
}
export interface SelectionMode {
  /**
   * Specifies whether column selection gestures are enabled on the Table.
   */
  column: string;
  /**
   * Specifies whether row selection gestures are enabled on the Table.
   */
  row: string;
}
export interface ScrollPolicyOptions {
  /**
   * The number of records the Table will request during each data fetch.
   */
  fetchSize: number;
}
export interface ContextMenuConfig {
  /**
   *
   */
  accessibleLabel: string;
}
